import api from "./axiosClient";

export type CreateChangelogEntryPayload = {
    tenantIds: string[];
    date: string;
    updateText: string;
};

export type UpdateChangelogEntryPayload = CreateChangelogEntryPayload;

export type ChangelogEntry = {
    id: string;
    tenantIds: string[];
    date: string;
    updateText: string;
    isNew: boolean;
    createdAt: string;
    updatedAt: string;
};

export type ChangelogEntryListResult = {
    entries: ChangelogEntry[];
    hasNew: boolean;
};

const asRecord = (value: unknown): Record<string, unknown> | null =>
    value && typeof value === "object" && !Array.isArray(value)
        ? value as Record<string, unknown>
        : null;

const asString = (value: unknown): string => typeof value === "string" ? value : "";

const normalizeChangelogEntry = (value: unknown): ChangelogEntry => {
    const record = asRecord(value) || {};
    return {
        id: asString(record.id),
        tenantIds: Array.isArray(record.tenantIds) ? record.tenantIds.map(String) : [],
        date: asString(record.date),
        updateText: asString(record.updateText),
        isNew: record.isNew === true,
        createdAt: asString(record.createdAt),
        updatedAt: asString(record.updatedAt),
    };
};

const changelogService = {
    async getStatus(): Promise<{ hasNew: boolean }> {
        const response = await api.get("/changelog/status");
        const responseRecord = asRecord(response.data);
        return { hasNew: responseRecord?.hasNew === true };
    },

    async listEntries(): Promise<ChangelogEntryListResult> {
        const response = await api.get("/changelog/entries");
        const responseRecord = asRecord(response.data);
        const data = responseRecord?.data;
        return {
            entries: Array.isArray(data) ? data.map(normalizeChangelogEntry) : [],
            hasNew: responseRecord?.hasNew === true,
        };
    },

    async createEntry(payload: CreateChangelogEntryPayload): Promise<ChangelogEntry> {
        const response = await api.post("/changelog/entries", payload);
        const responseRecord = asRecord(response.data);
        return normalizeChangelogEntry(responseRecord?.data);
    },

    async updateEntry(entryId: string, payload: UpdateChangelogEntryPayload): Promise<ChangelogEntry> {
        const response = await api.put(`/changelog/entries/${entryId}`, payload);
        const responseRecord = asRecord(response.data);
        return normalizeChangelogEntry(responseRecord?.data);
    },

    async deleteEntry(entryId: string): Promise<void> {
        await api.delete(`/changelog/entries/${entryId}`);
    },

    async markSeen(): Promise<void> {
        await api.post("/changelog/seen");
    },
};

export default changelogService;
